import * as React from "react";

import { FleetOverview } from "api/fleet";
import { Card, Skeleton } from "Components/ui";
import { Building2, CircleCheck, TriangleAlert } from "Components/ui/icons";
import { cn } from "utils/cn";
import { pluralize } from "utils/format";
import { formatTimeOfDay } from "../format";

type Summary = FleetOverview["summary"];
type VerdictTone = "success" | "warning" | "danger" | "neutral";

const CHIP: Record<VerdictTone, string> = {
  success: "bg-success-subtle text-success-text",
  warning: "bg-warning-subtle text-warning-text",
  danger: "bg-danger-subtle text-danger-text",
  neutral: "bg-surface-muted text-muted-foreground",
};

const COUNTS: { key: keyof Omit<Summary, "total">; label: string; dot: string }[] = [
  { key: "up", label: "Up", dot: "bg-success" },
  { key: "degraded", label: "Degraded", dot: "bg-warning" },
  { key: "down", label: "Down", dot: "bg-danger" },
  { key: "unknown", label: "Unknown", dot: "bg-subtle-foreground" },
];

interface Verdict {
  tone: VerdictTone;
  icon: React.ReactNode;
  headline: string;
  detail: string;
}

/** One sentence that answers "is the fleet OK?", worked out from the live summary. */
const verdictFor = (summary: Summary, lastCheckedAt: string | null): Verdict => {
  const checked = lastCheckedAt
    ? `Last health check at ${formatTimeOfDay(lastCheckedAt)} IST.`
    : "No health check has run yet.";
  if (summary.total === 0) {
    return {
      tone: "neutral",
      icon: <Building2 />,
      headline: "No tenants yet",
      detail: "Add or import a tenant to see its health here.",
    };
  }
  const attention = summary.down + summary.degraded + summary.unknown;
  if (attention === 0) {
    return {
      tone: "success",
      icon: <CircleCheck />,
      headline:
        summary.total === 1
          ? "The tenant is answering"
          : `All ${summary.total} tenants are answering`,
      detail: checked,
    };
  }
  const parts = [
    summary.down > 0 && `${summary.down} down`,
    summary.degraded > 0 && `${summary.degraded} degraded`,
    summary.unknown > 0 && `${summary.unknown} unknown`,
  ].filter(Boolean);
  return {
    tone: summary.down > 0 ? "danger" : "warning",
    icon: <TriangleAlert />,
    headline: `${attention} of ${pluralize(summary.total, "tenant")} ${
      attention === 1 ? "needs" : "need"
    } attention`,
    detail: `${parts.join(", ")}, listed first on the board below. ${checked}`,
  };
};

interface FleetVerdictProps {
  summary: Summary | undefined;
  lastCheckedAt: string | null;
  loading: boolean;
  className?: string;
}

/** The dashboard's opening line: the fleet's state in words, then the count per status. */
const FleetVerdict: React.FC<FleetVerdictProps> = ({
  summary,
  lastCheckedAt,
  loading,
  className,
}) => {
  if (loading || !summary) {
    return (
      <Card className={cn("flex items-center gap-4 p-5", className)} aria-busy="true">
        <Skeleton className="h-11 w-11 rounded-card" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-6 w-72 max-w-full" />
          <Skeleton className="h-4 w-56 max-w-full" />
        </div>
      </Card>
    );
  }

  const verdict = verdictFor(summary, lastCheckedAt);
  return (
    <Card
      className={cn(
        "flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between",
        className
      )}
    >
      <div role="status" className="flex min-w-0 items-start gap-4">
        <span
          aria-hidden="true"
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-card [&_svg]:h-6 [&_svg]:w-6",
            CHIP[verdict.tone]
          )}
        >
          {verdict.icon}
        </span>
        <div className="min-w-0 space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">{verdict.headline}</h2>
          <p className="text-sm text-muted-foreground">{verdict.detail}</p>
        </div>
      </div>
      <dl className="grid shrink-0 grid-cols-4 gap-x-6 sm:gap-x-8">
        {COUNTS.map(({ key, label, dot }) => {
          const count = summary[key];
          return (
            <div key={key} className="min-w-0">
              <dt className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                <span
                  aria-hidden="true"
                  className={cn("h-2 w-2 shrink-0 rounded-full", dot, count === 0 && "opacity-40")}
                />
                {label}
              </dt>
              <dd
                className={cn(
                  "mt-0.5 text-2xl font-semibold tracking-tight",
                  count === 0 && "text-subtle-foreground"
                )}
              >
                {count}
              </dd>
            </div>
          );
        })}
      </dl>
    </Card>
  );
};

export default FleetVerdict;
